// apps/web/src/features/clients/components/RequisitesInspectPanel.tsx
//
// What the entity's requisites sets look like RIGHT NOW, shown inside the editor
// after a create whose outcome the client never learned (AC-9).
//
// INVARIANT: this panel decides nothing. It never matches the draft against a
// row by text and never claims the create did or did not land — it puts the
// refreshed, id-addressed sets in front of the operator, who is the only party
// that can tell whether the one they typed is among them. The draft stays in
// the form the whole time.
import { useTranslation } from 'react-i18next';
import type { LegalEntityRequisites } from '@magimp/shared';
import { Button, StatusBadge } from '../../../ui/index.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';

export interface RequisitesInspectPanelProps {
  entityName: string;
  /** Sets of the owning entity as of the last successful refresh. */
  sets: readonly LegalEntityRequisites[];
  /**
   * Create mode only: the deliberate way out. Seeing the list is not consent to
   * send the create again, so this is a SEPARATE decision with its own words —
   * the ordinary Save path stays blocked either way.
   */
  onCreateAnyway?: (() => void) | undefined;
}

/** First non-empty line of a set's text — enough to recognise it, not a match. */
function firstLine(details: string): string {
  return details.split('\n').find((line) => line.trim() !== '') ?? '';
}

export function RequisitesInspectPanel({
  entityName,
  sets,
  onCreateAnyway,
}: RequisitesInspectPanelProps) {
  const { t } = useTranslation(['clients']);
  const actionStyle = useTouchActionStyle();
  return (
    <section
      data-testid="requisites-inspect"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
        margin: '0 0 8px',
        padding: 'var(--s-4)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-2)',
        minWidth: 0,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 'var(--fz-13)', fontWeight: 600 }}>
        {t('clients:requisites.inspectTitle', { entity: entityName })}
      </h3>
      <p style={{ margin: 0, fontSize: 'var(--fz-12)', color: 'var(--fg-2)' }}>
        {t('clients:requisites.inspectHelp')}
      </p>
      {sets.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>
          {t('clients:requisites.inspectEmpty')}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {sets.map((set, i) => (
            <li
              key={set.id}
              data-testid="requisites-inspect-set"
              style={{
                display: 'flex',
                gap: 'var(--s-2)',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                fontSize: 'var(--fz-12)',
                minWidth: 0,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {t('clients:requisites.setOrdinal', { n: i + 1 })}
              </span>
              <span style={{ color: 'var(--fg-3)', overflowWrap: 'anywhere', minWidth: 0 }}>
                {firstLine(set.details)}
              </span>
              {set.archivedAt && <StatusBadge status="archived" size="sm" />}
            </li>
          ))}
        </ul>
      )}
      {onCreateAnyway && (
        <div>
          <Button
            size="sm"
            style={actionStyle}
            onClick={onCreateAnyway}
            data-testid="requisites-create-anyway"
          >
            {t('clients:actions.createAnyway')}
          </Button>
        </div>
      )}
    </section>
  );
}
